/**
 * MobileService.js
 *
 * Handles communication with the backend REST services specifically for fetching,
 * decompressing, and saving mobile form layout and settings data.
 */

import listService from "ListService";
import formSaveService from 'FormSaveService';
import undoService from 'UndoService.js';
import formulaService from "../xml/FormulaService";
import xjService from "../xml/XJService";
import Immutable from 'immutable';

import notificationService from "NotificationService";

import Spinner from 'spin/spin';
import constants from 'Constants';
import restMobileService from 'RestMobileService';
import zLib from 'util/ZLib.js';
import Base64Decoder from 'util/Base64Decoder';
import util from 'util/Util.js'

import mobileLoadService from "./MobileLoadService";
import { ensureArray } from '../../components/Common/Utils';

class MobileService {

    async initializeMobileData() {
        const formId = listService.formBase.id;
        const majorVersion = listService.formBase.majorVersion;
        const minorVersion = listService.formBase.minorVersion;

        try {
            const form1Data = await this.openMobileForm(formId, majorVersion, minorVersion);

            if (form1Data.isWizardCompleted !== null && util.isEmpty(listService.mobileGetWizardCompleted)) {
                listService.mobileSetWizardCompleted = form1Data.isWizardCompleted;
            }

            if (form1Data?.mobileForm?.length) {

                const form2Data = await this.getTwoColumnsConfig(formId, majorVersion, minorVersion);

                const mobileSettings = form1Data.mobileSettings?.length ? form1Data.mobileSettings[0] : null;                
                mobileLoadService.initializeMobileStylesAndSettings(mobileSettings);
                mobileLoadService.initializeMobileLayouts(form1Data, form2Data);

                const maxAppWidth = form1Data.mobileForm[0].Form.Properties["_maxAppWidth"];
                listService.currentForm.model.set('maxAppWidth', maxAppWidth, constants.MODEL_SILENT);

            }

        } catch (err) {
            console.error("Failed to initialize mobile data:", err);
            notificationService.error(`Error initializing mobile data: ${err.message || err}`);
        }
    }

    _processXml(xmlString) {
        if (!xmlString) return null;
        try {
            const minifiedXml = util.minifyXMLString(xmlString);
            const doc = formulaService.preprocess(minifiedXml);
            return xjService.xmlDocToJs(doc);
        } catch (error) {
            console.error("Error processing XML:", error);
            return null;
        }
    }

    async _fetchAndProcessMobileData(formId, majorVersion, minorVersion, mobileViewTypeId, includeSettings = false) {
        const result = { mobileForm: [], mobileSettings: [], isWizardCompleted: null };

        try {
            const data = await restMobileService.openMobileForm(formId, majorVersion, minorVersion, mobileViewTypeId);

            if (data.FormXML) {
                const formXml = this._decompressForm(data.FormXML);
                const formObj = this._processXml(formXml);
                if (formObj) {
                    result.mobileForm.push(formObj);
                }
            }

            if (includeSettings && data.MobileSettingsXML) {
                const settingsXML = this._decompressForm(data.MobileSettingsXML);
                const settingsObj = this._processXml(settingsXML);
                if (settingsObj) {
                    result.mobileSettings.push(settingsObj);
                }
            }

            if (mobileViewTypeId === 1 && data.IsWizardCompleted !== undefined) {
                result.isWizardCompleted = util.isTrue(data.IsWizardCompleted);
                if (util.isEmpty(listService.mobileSetWizardCompleted)) {
                    listService.mobileSetWizardCompleted = result.isWizardCompleted;
                }
            }

            return result;

        } catch (err) {
            console.error(`Error fetching mobile form type ${mobileViewTypeId}:`, err);
            notificationService.error(`Error getting mobile app (type ${mobileViewTypeId}): ${err.message || err}`);
            throw err;
        }
    }

    

    async openMobileForm(formId, majorVersion, minorVersion) {
        return await this._fetchAndProcessMobileData(formId, majorVersion, minorVersion, 1, true);
    }

    async getTwoColumnsConfig(formId, majorVersion, minorVersion) {
        return await this._fetchAndProcessMobileData(formId, majorVersion, minorVersion, 2, false);
    }

    

    _decompressForm(rawForm) {
        const isCompressed = 'H4' === rawForm.slice(0, 2);
        return isCompressed ? zLib.decompressForm(rawForm) : Base64Decoder.decodeToString(rawForm);
    }


    async saveApp(options = {}) {
        const { isNewVersion = false } = options;

        listService.isMobileDirty = true;
        const spinner = new Spinner().spin($(constants.CONTENT_ID).get(0));

        try {
            await formSaveService.handleSave({ isNewVersion });
    
            listService.isMobileDirty = false;
            undoService.clearChangeTracker();
            
            return "App successfully saved.";
    
        } catch (errorInfo) {            
            throw errorInfo;     
        } finally {
            spinner.stop();
        }
    }

    designerHasNewObjects(){
        const objectList = listService.objectList;
        const mobileObjects = listService.mobileObjects;
    
        const sourceValues = Array.from(objectList.values()).filter(obj => obj.id !== 'aForm');
        const mobileValues = Array.from(mobileObjects.values());
        
        if (sourceValues.length > mobileValues.length) {
            return true; 
        }
        const missingItems = sourceValues.some(sourceObj => {
            const mobileObj = mobileObjects.get(sourceObj.model.id);
    
            // It counts as "Missing" if: It is undefined (not in the map) OR it exists, but isNew is true
            return !mobileObj || mobileObj.isNew === true;
        });
        return missingItems;
    }
}

export default new MobileService();